import React, { useState } from 'react'
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@material-ui/core'

import { Dialog, snackbar } from 'components'
import { useApiCall, useApi } from 'hooks'
import { ERRORS, STRINGS } from 'consts'
import { hasRole, Role as RoleEnum } from 'utils/roles'
import { ConfigUtils } from 'utils/config'
import { GetStoresResponse, Store } from 'features/Main/Stores/types'
import { getStores } from 'features/Main/Stores/api'

import { Admin, Role, UpdateAdminRolesData } from '../../types'
import { updateAdminRoles } from '../../api'
import { roleLabelsMap } from '../../constants'
import { useStyles } from './styles'

interface Props {
  admin: Admin
  handleClose: () => void
  onDone: () => void
  roles: Role[]
  currentAdmin?: Admin
}

const UpdateAdminDialog: React.FC<Props> = ({
  admin,
  handleClose,
  onDone,
  roles,
  currentAdmin,
}) => {
  const classes = useStyles()

  const [updateAdminApi, isUpdateAdminLoading] = useApiCall<UpdateAdminRolesData, void>(
    updateAdminRoles,
  )

  const isSuperAdmin = hasRole(currentAdmin, [RoleEnum.SuperAdmin])
  const filteredRoles = isSuperAdmin
    ? roles
    : roles.filter(role => role.name !== 'super_admin' && role.name !== 'admin')

  // Obtener el primer rol (ahora solo hay uno)
  const currentRoleId = admin.roles && admin.roles.length > 0 ? admin.roles[0].id : 0
  const [selectedRole, setSelectedRole] = useState<number>(currentRoleId)
  // Obtener stores actuales del admin
  const currentStoreIds = admin.stores ? admin.stores.map(s => s.id) : []
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>(currentStoreIds)
  const [selectedStoreId, setSelectedStoreId] = useState<number>(
    currentStoreIds.length > 0 ? currentStoreIds[0] : 0,
  ) // Para employee (single select)

  // Password fields (only for super admin)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')

  // Report settings
  const [reportsEnabled, setReportsEnabled] = useState<boolean>(admin.reportsEnabled ?? false)
  const [reviewsCanRespond, setReviewsCanRespond] = useState<boolean>(
    admin.reviewsCanRespond ?? false,
  )
  const [criticalReviewAlertsEnabled, setCriticalReviewAlertsEnabled] = useState<boolean>(
    admin.criticalReviewAlertsEnabled ?? false,
  )
  const [selectedReportFrequencies, setSelectedReportFrequencies] = useState<string[]>(() => {
    const frequencies: string[] = []
    if (admin.reportFrequencyWeekly) frequencies.push('weekly')
    if (admin.reportFrequencyMonthly) frequencies.push('monthly')
    return frequencies
  })

  // Cargar stores
  const getStoresResponseGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []
  const [{ data: stores, isLoading: isLoadingStores }] = useApi<GetStoresResponse, Store[]>(
    getStores,
    getStoresResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  // Detectar si tiene todos los stores (comparar con la lista completa)
  const allStoreIds = stores.map(s => s.id).sort()
  const currentStoreIdsSorted = [...currentStoreIds].sort()
  const hasAllStores =
    stores.length > 0 &&
    currentStoreIdsSorted.length === allStoreIds.length &&
    currentStoreIdsSorted.every((id, index) => id === allStoreIds[index])
  const [allStoresSelected, setAllStoresSelected] = useState<boolean>(hasAllStores)
  const [userInteracted, setUserInteracted] = useState<boolean>(false)

  const handleRoleSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target
    const newRoleId = typeof value === 'string' ? parseInt(value, 10) : (value as number)
    setSelectedRole(newRoleId)
    // Reset stores cuando cambia el rol
    setSelectedStoreIds([])
    setSelectedStoreId(0)
    setAllStoresSelected(false)
    setReviewsCanRespond(false)
    setUserInteracted(false) // Reset el flag de interacción del usuario
  }

  const handleStoreSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedStoreIds(event.target.value as number[])
  }

  const handleStoreSelectSingle = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedStoreId(event.target.value as number)
  }

  const handleAllStoresToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setUserInteracted(true)
    setAllStoresSelected(checked)
    if (checked) {
      setSelectedStoreIds([]) // Limpiar selección cuando se marca "todos"
    } else if (currentStoreIds.length > 0) {
      // Si desmarca "todos", restaurar los stores que tenía originalmente
      setSelectedStoreIds([...currentStoreIds])
    }
  }

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value
    setPassword(newPassword)
    if (newPassword && newPassword.length < STRINGS.PASSWORD_MIN_LENGTH) {
      setPasswordError(
        `La contraseña debe ser al menos de ${STRINGS.PASSWORD_MIN_LENGTH} caracteres.`,
      )
    } else {
      setPasswordError('')
    }
    // Re-validate confirm password
    if (confirmPassword && newPassword !== confirmPassword) {
      setConfirmPasswordError('Las contraseñas deben coincidir.')
    } else if (confirmPassword && newPassword === confirmPassword) {
      setConfirmPasswordError('')
    }
  }

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = event.target.value
    setConfirmPassword(newConfirmPassword)
    if (newConfirmPassword && newConfirmPassword !== password) {
      setConfirmPasswordError('Las contraseñas deben coincidir.')
    } else {
      setConfirmPasswordError('')
    }
  }

  // Verificar según el rol
  const selectedRoleName = filteredRoles.find(r => r.id === selectedRole)?.name
  const isEmployee = selectedRoleName === 'employee'
  const isAnalyst = selectedRoleName === 'analyst'
  const isMarketing = selectedRoleName === 'marketing'
  const canConfigureReviewsRespond = isAnalyst || isMarketing
  const storeIdsError = isEmployee && selectedStoreId === 0
  const storeIdsErrorAnalyst = isAnalyst && !allStoresSelected && selectedStoreIds.length === 0
  const storesNotLoadedError = isAnalyst && allStoresSelected && (!stores || stores.length === 0)

  // Inicializar allStoresSelected solo una vez cuando se cargan los stores
  // No actualizar si el usuario ya ha interactuado con el checkbox
  React.useEffect(() => {
    if (stores.length > 0 && isAnalyst && currentStoreIds.length > 0 && !userInteracted) {
      const allStoreIdsCurrent = stores.map(s => s.id).sort()
      const currentStoreIdsSortedEffect = [...currentStoreIds].sort()
      const hasAllStoresCurrent =
        currentStoreIdsSortedEffect.length === allStoreIdsCurrent.length &&
        currentStoreIdsSortedEffect.every((id, index) => id === allStoreIdsCurrent[index])
      if (hasAllStoresCurrent !== allStoresSelected) {
        setAllStoresSelected(hasAllStoresCurrent)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores.length, isAnalyst])

  // Verificar si hubo cambios: rol cambió o (si es empleado/analyst, los stores cambiaron)
  const roleChanged = selectedRole !== currentRoleId
  const { roles: adminRolesList } = admin
  const currentRoleName = adminRolesList && adminRolesList.length > 0 ? adminRolesList[0].name : ''
  const currentRoleIsEmployee = currentRoleName === 'employee'
  const currentRoleIsAnalyst = currentRoleName === 'analyst'

  let storeIdsChanged = false
  if (isEmployee) {
    storeIdsChanged = selectedStoreId !== (currentStoreIds.length > 0 ? currentStoreIds[0] : 0)
  } else if (isAnalyst) {
    const allStoreIdsCurrent = stores.map(s => s.id).sort()
    const currentStoreIdsSortedChange = [...currentStoreIds].sort()
    const hasAllStoresCurrent =
      currentStoreIdsSortedChange.length === allStoreIdsCurrent.length &&
      currentStoreIdsSortedChange.every((id, index) => id === allStoreIdsCurrent[index])

    if (allStoresSelected && hasAllStoresCurrent) {
      storeIdsChanged = false // Ambos son "todos"
    } else if (!allStoresSelected && !hasAllStoresCurrent) {
      storeIdsChanged =
        JSON.stringify(selectedStoreIds.sort()) !== JSON.stringify(currentStoreIds.sort())
    } else {
      storeIdsChanged = true // Cambió de "todos" a específicos o viceversa
    }
  }

  // Password validation - only validate if password is provided
  const isConfirmPasswordMissing = isSuperAdmin && password !== '' && confirmPassword === ''
  const hasPasswordErrors =
    isSuperAdmin &&
    password !== '' &&
    (passwordError !== '' ||
      isConfirmPasswordMissing ||
      confirmPasswordError !== '' ||
      (confirmPassword !== '' && password !== confirmPassword))
  const passwordChanged = isSuperAdmin && password !== ''

  const reportWeeklyChanged =
    (admin.reportFrequencyWeekly ?? false) !== selectedReportFrequencies.includes('weekly')
  const reportMonthlyChanged =
    (admin.reportFrequencyMonthly ?? false) !== selectedReportFrequencies.includes('monthly')
  const reportsEnabledChanged = (admin.reportsEnabled ?? false) !== reportsEnabled
  const reviewsCanRespondChanged = (admin.reviewsCanRespond ?? false) !== reviewsCanRespond
  const criticalReviewAlertsChanged =
    (admin.criticalReviewAlertsEnabled ?? false) !== criticalReviewAlertsEnabled

  const isUntouched =
    !roleChanged &&
    !passwordChanged &&
    !reportsEnabledChanged &&
    !reviewsCanRespondChanged &&
    !criticalReviewAlertsChanged &&
    !reportWeeklyChanged &&
    !reportMonthlyChanged &&
    ((!isEmployee && !isAnalyst) ||
      (currentRoleIsEmployee && isEmployee && !storeIdsChanged) ||
      (currentRoleIsAnalyst && isAnalyst && !storeIdsChanged))
  const isSubmitDisabled =
    isUpdateAdminLoading ||
    isLoadingStores ||
    isUntouched ||
    selectedRole === 0 ||
    storeIdsError ||
    storeIdsErrorAnalyst ||
    storesNotLoadedError ||
    hasPasswordErrors

  const onUpdateAdmin = async () => {
    if (selectedRole === 0) {
      snackbar.show('Debe seleccionar un rol')
      return
    }
    if (isEmployee && selectedStoreId === 0) {
      snackbar.show('Debe seleccionar un local para empleados')
      return
    }
    if (isAnalyst && !allStoresSelected && selectedStoreIds.length === 0) {
      snackbar.show('Debe seleccionar "Todos los locales" o al menos un local')
      return
    }
    // Validate password if provided
    if (isSuperAdmin && password) {
      if (password.length < STRINGS.PASSWORD_MIN_LENGTH) {
        snackbar.show(
          `La contraseña debe ser al menos de ${STRINGS.PASSWORD_MIN_LENGTH} caracteres.`,
        )
        return
      }
      if (!confirmPassword) {
        snackbar.show('Debe repetir la nueva contraseña.')
        return
      }
      if (password !== confirmPassword) {
        snackbar.show('Las contraseñas deben coincidir.')
        return
      }
    }
    try {
      let storeIdsToSend: number[] | undefined

      if (isEmployee) {
        if (selectedStoreId > 0) {
          storeIdsToSend = [selectedStoreId]
        }
      } else if (isAnalyst) {
        if (allStoresSelected) {
          // Enviar los IDs de todas las tiendas disponibles en el selector
          if (stores && stores.length > 0) {
            storeIdsToSend = stores.map(s => s.id)
          } else {
            snackbar.show(
              'Error: No se pudieron cargar las tiendas. Por favor, intente nuevamente.',
            )
            return
          }
        } else if (selectedStoreIds.length > 0) {
          storeIdsToSend = selectedStoreIds
        }
      }

      const hasWeekly = selectedReportFrequencies.includes('weekly')
      const hasMonthly = selectedReportFrequencies.includes('monthly')

      await updateAdminApi({
        id: admin.id,
        payload: selectedRole,
        storeIds: storeIdsToSend,
        ...(isSuperAdmin &&
        password &&
        password.length >= STRINGS.PASSWORD_MIN_LENGTH &&
        password === confirmPassword
          ? { password }
          : {}),
        reportsEnabled,
        reportFrequencyWeekly: reportsEnabled ? hasWeekly : false,
        reportFrequencyBiweekly: false,
        reportFrequencyMonthly: reportsEnabled ? hasMonthly : false,
        ...(canConfigureReviewsRespond && ConfigUtils.reviews.enabled ? { reviewsCanRespond } : {}),
        ...(ConfigUtils.reviews.enabled ? { criticalReviewAlertsEnabled } : {}),
      })
      onDone()
    } catch (err) {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  return (
    <Dialog
      title="Editar administrador"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isUpdateAdminLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onUpdateAdmin}
      contentClassName={classes.dialogContent}
      className={classes.dialogContainer}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Rol</InputLabel>
            <Select
              value={selectedRole}
              onChange={handleRoleSelect}
              fullWidth
              disabled={
                !isSuperAdmin &&
                (filteredRoles.find(r => r.id === selectedRole)?.name === 'super_admin' ||
                  filteredRoles.find(r => r.id === selectedRole)?.name === 'admin')
              }
            >
              {filteredRoles.map(role => (
                <MenuItem key={role.id} value={role.id}>
                  {roleLabelsMap[role.name]?.label || role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {isEmployee && (
          <Grid item xs={12}>
            <FormControl fullWidth error={storeIdsError}>
              <InputLabel>Local *</InputLabel>
              <Select value={selectedStoreId} onChange={handleStoreSelectSingle} fullWidth required>
                <MenuItem value={0}>
                  <em>Seleccionar local</em>
                </MenuItem>
                {stores.map(store => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.id} - {store.name}
                  </MenuItem>
                ))}
              </Select>
              {storeIdsError && (
                <span style={{ color: 'red', fontSize: '12px' }}>
                  Debe seleccionarse un local para empleados
                </span>
              )}
            </FormControl>
          </Grid>
        )}
        {isAnalyst && (
          <Grid item xs={12}>
            <FormControl fullWidth error={storeIdsErrorAnalyst}>
              {!allStoresSelected && (
                <>
                  <InputLabel>Locales *</InputLabel>
                  <Select
                    multiple
                    value={selectedStoreIds}
                    onChange={handleStoreSelect}
                    fullWidth
                    required
                    renderValue={selected =>
                      (selected as number[])
                        .map(id => {
                          const store = stores.find(s => s.id === id)
                          return store ? `${store.id} - ${store.name}` : ''
                        })
                        .join(', ')
                    }
                  >
                    {stores.map(store => (
                      <MenuItem key={store.id} value={store.id}>
                        <Checkbox checked={selectedStoreIds.indexOf(store.id) > -1} />
                        {store.id} - {store.name}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allStoresSelected}
                    onChange={handleAllStoresToggle}
                    color="primary"
                  />
                }
                label="Todos los locales"
              />
              {storeIdsErrorAnalyst && (
                <span style={{ color: 'red', fontSize: '12px' }}>
                  Debe seleccionarse &quot;Todos los locales&quot; o al menos un local
                </span>
              )}
              {storesNotLoadedError && (
                <span style={{ color: 'red', fontSize: '12px' }}>
                  Cargando tiendas, por favor espere...
                </span>
              )}
            </FormControl>
          </Grid>
        )}
        {isSuperAdmin && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nueva contraseña"
                type="password"
                fullWidth
                autoComplete="new-password"
                name="new-password"
                value={password}
                onChange={handlePasswordChange}
                error={passwordError !== ''}
                helperText={passwordError}
                disabled={isUpdateAdminLoading}
                placeholder="Dejar vacío para no cambiar"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Repetir contraseña"
                type="password"
                fullWidth
                autoComplete="new-password"
                name="confirm-new-password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                error={confirmPasswordError !== '' || isConfirmPasswordMissing}
                helperText={
                  isConfirmPasswordMissing
                    ? 'Debe repetir la nueva contraseña.'
                    : confirmPasswordError
                }
                disabled={isUpdateAdminLoading}
                placeholder="Dejar vacío para no cambiar"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={reportsEnabled}
                onChange={e => {
                  const { checked } = e.target
                  setReportsEnabled(checked)
                  if (!checked) {
                    setSelectedReportFrequencies([])
                  }
                }}
                color="primary"
              />
            }
            label="Recibir reportes automáticos del dashboard"
          />
          {reportsEnabled && (
            <FormControl fullWidth style={{ marginTop: 8 }}>
              <InputLabel>Frecuencias de reporte</InputLabel>
              <Select
                multiple
                value={selectedReportFrequencies}
                onChange={e => setSelectedReportFrequencies(e.target.value as string[])}
                renderValue={selected =>
                  (selected as string[])
                    .map(value => {
                      if (value === 'weekly') return 'Reporte semanal'
                      if (value === 'monthly') return 'Reporte mensual (cierre de mes)'
                      return value
                    })
                    .join(', ')
                }
              >
                <MenuItem value="weekly">
                  <Checkbox checked={selectedReportFrequencies.includes('weekly')} />
                  Reporte semanal
                </MenuItem>
                <MenuItem value="monthly">
                  <Checkbox checked={selectedReportFrequencies.includes('monthly')} />
                  Reporte mensual (cierre de mes)
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
        {ConfigUtils.reviews.enabled && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={criticalReviewAlertsEnabled}
                  onChange={e => setCriticalReviewAlertsEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Recibir alertas de reseñas negativas"
            />
          </Grid>
        )}
        {canConfigureReviewsRespond && ConfigUtils.reviews.enabled && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Permisos de Reseñas
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reviewsCanRespond}
                  onChange={e => setReviewsCanRespond(e.target.checked)}
                  color="primary"
                />
              }
              label="Permitir responder reseñas"
            />
            {!reviewsCanRespond && (
              <Typography variant="caption" color="textSecondary" display="block">
                Solo Vista — acceso de lectura sin poder modificar reseñas
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

UpdateAdminDialog.defaultProps = {
  currentAdmin: undefined,
}

export { UpdateAdminDialog }
